/**
 * AiProposal — Tarjetas editables con checkboxes para el flujo
 * "Proponer → Debatir → Ejecutar" (Doc IA §3).
 *
 * Props:
 *  - acciones:  [{accion, descripcion, datos, avisa, tipo_badge}]
 *  - onEjecutar: callback(resultadoEjecucion)
 *  - onCancelar: callback()
 */
import React, { useState } from 'react';
import { iaApi } from '../services/api';
import { Button } from './ui/button';
import { CheckCircle2, AlertTriangle, Pencil, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BADGE_COLORS = {
  Crear: 'bg-emerald-100 text-emerald-700',
  Modificar: 'bg-amber-100 text-amber-700',
};

export default function AiProposal({ acciones: accionesInit, onEjecutar, onCancelar }) {
  // Cada acción tiene selected + posibilidad de editar descripción
  const [items, setItems] = useState(
    (accionesInit || []).map((a, i) => ({ ...a, _id: i, selected: true, _editing: false }))
  );
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const toggle = (i) => setItems((s) => s.map((x) => (x._id === i ? { ...x, selected: !x.selected } : x)));
  const editarDescripcion = (i, val) => setItems((s) => s.map((x) => x._id === i ? { ...x, descripcion: val, datos: { ...x.datos, descripcion: val } } : x));
  const setEditing = (i, v) => setItems((s) => s.map((x) => x._id === i ? { ...x, _editing: v } : x));

  const ejecutar = async (solo = 'seleccionados') => {
    const payload = items
      .filter((x) => solo === 'todas' || x.selected)
      .map(({ accion, descripcion, datos }) => ({ accion, descripcion, datos }));
    if (!payload.length) { toast.error('Selecciona al menos una acción'); return; }
    setLoading(true);
    try {
      const res = await iaApi.ejecutar(payload, false);
      setLastResult(res);
      const ok = (res.ejecutadas || []).length;
      const err = (res.errores || []).length;
      if (ok) toast.success(`${ok} acción(es) ejecutada(s)`);
      if (err) toast.error(`${err} acción(es) fallaron`);
      if (onEjecutar) onEjecutar(res);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Si ya se ejecutó, mostrar resumen y botón cerrar
  if (lastResult) {
    return (
      <div className="space-y-3" data-testid="ai-proposal-result">
        <div className="flex items-center gap-2 text-emerald-700">
          <CheckCircle2 className="w-5 h-5" />
          <p className="font-semibold">Acciones ejecutadas</p>
        </div>
        <ul className="text-sm space-y-1 text-ink-700">
          {(lastResult.ejecutadas || []).map((e, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-emerald-600">✓</span>
              <span>{e.resumen}</span>
            </li>
          ))}
          {(lastResult.errores || []).map((e, i) => (
            <li key={`e${i}`} className="flex items-start gap-2 text-rose-600">
              <span>✗</span>
              <span>{e.accion}: {e.error}</span>
            </li>
          ))}
        </ul>
        <div className="pt-2 flex justify-end">
          <Button onClick={onCancelar} variant="outline" size="sm">Cerrar</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="ai-proposal">
      <div className="flex items-center gap-2 text-brand-700">
        <Sparkles className="w-4 h-4" />
        <p className="text-sm font-semibold">Propuesta de la IA</p>
        <span className="text-xs text-ink-500">· Revisa, edita o quita antes de ejecutar</span>
      </div>

      <div className="space-y-2">
        {items.map((a) => {
          const badge = a.tipo_badge || (a.accion?.startsWith('modificar') ? 'Modificar' : 'Crear');
          const color = BADGE_COLORS[badge] || BADGE_COLORS.Crear;
          return (
            <div
              key={a._id}
              className={`border rounded-md p-3 transition-colors ${a.selected ? 'border-brand-200 bg-brand-50/30' : 'border-ink-200 bg-white'}`}
              data-testid={`ai-proposal-item-${a._id}`}
            >
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={a.selected}
                  onChange={() => toggle(a._id)}
                  className="mt-1"
                  data-testid={`ai-proposal-check-${a._id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${color}`}>{badge}</span>
                    {a.avisa && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 flex items-center gap-1">
                        <AlertTriangle className="w-2.5 h-2.5" /> Modifica dato existente
                      </span>
                    )}
                    {a.datos?.prioridad && (
                      <span className="text-[10px] text-ink-500">prio: {a.datos.prioridad}</span>
                    )}
                    {a.datos?.fecha_vencimiento && (
                      <span className="text-[10px] text-ink-500">fecha: {a.datos.fecha_vencimiento}</span>
                    )}
                  </div>
                  {a._editing ? (
                    <input
                      className="w-full mt-1.5 px-2 py-1 text-sm rounded border border-brand-300"
                      value={a.descripcion || ''}
                      onChange={(e) => editarDescripcion(a._id, e.target.value)}
                      onBlur={() => setEditing(a._id, false)}
                      autoFocus
                    />
                  ) : (
                    <p className="text-sm text-ink-900 mt-1" onClick={() => setEditing(a._id, true)}>
                      {a.descripcion || a.accion}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setEditing(a._id, !a._editing)}
                  className="text-ink-400 hover:text-ink-900"
                  title="Editar"
                >
                  <Pencil className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex gap-2 pt-2 border-t border-ink-100">
        <Button variant="outline" size="sm" onClick={onCancelar} disabled={loading}>
          Cancelar
        </Button>
        <div className="flex-1" />
        <Button
          size="sm"
          onClick={() => ejecutar('seleccionados')}
          disabled={loading || !items.some((x) => x.selected)}
          data-testid="ai-ejecutar-seleccionados"
        >
          {loading && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
          Ejecutar seleccionados ({items.filter((x) => x.selected).length})
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={() => ejecutar('todas')}
          disabled={loading}
          data-testid="ai-ejecutar-todas"
          className="bg-brand-700 hover:bg-brand-800"
        >
          Ejecutar todo
        </Button>
      </div>
    </div>
  );
}
